import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router';

import {
  isPersistableQueryKey,
  PERSISTED_QUERY_PARAMS_STORAGE_KEY
} from '@/config/persisted-query-params/constants';
import { isAppQueryWriteNavigation } from '@/config/persisted-query-params/navigationSource';
import { readBag } from '@/config/persisted-query-params/readBag';
import { resolveStorageKey } from '@/config/persisted-query-params/resolveStorageKey';
import { useApp } from '@/contexts/app-context';

const snapshotsEqual = (
  a: Record<string, string>,
  b: Record<string, string>
): boolean => {
  const aKeys = Object.keys(a);
  return (
    aKeys.length === Object.keys(b).length && aKeys.every(k => a[k] === b[k])
  );
};

const buildSnapshot = (
  searchParams: URLSearchParams
): Record<string, string> => {
  const snapshot: Record<string, string> = {};
  for (const [key, value] of searchParams.entries()) {
    if (isPersistableQueryKey(key)) snapshot[key] = value;
  }
  return snapshot;
};

const shouldSkipExternalMode = (
  snapshot: Record<string, string>,
  externalSnapshot: Record<string, string> | null
): boolean => {
  if (externalSnapshot === null) return false;
  return (
    Object.keys(snapshot).length === 0 ||
    snapshotsEqual(snapshot, externalSnapshot)
  );
};

const isUnauthorizedExternalUrl = (
  snapshot: Record<string, string>,
  existingEntry: Record<string, string> | undefined
): boolean => {
  return (
    Object.keys(snapshot).length > 0 &&
    (!existingEntry || !snapshotsEqual(snapshot, existingEntry))
  );
};

const usePersistedQueryParams = () => {
  const [searchParams] = useSearchParams();
  const { pathname, state } = useLocation();
  const navigationType = useNavigationType();
  const { selectedRegion, selectedCropSeason } = useApp();

  const storageKey = resolveStorageKey(pathname, {
    region: selectedRegion,
    cropSeason: selectedCropSeason
  });

  // Holds the param snapshot of a detected shared/external URL; writes are skipped while the URL matches it.
  const externalParamsRef = useRef<Record<string, string> | null>(null);
  const lastStorageKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const snapshot = buildSnapshot(searchParams);
    const bag = readBag();
    const existingEntry = bag[storageKey];
    // React Router restores location.state on a POP (browser back/forward), so a
    // history entry created by an earlier app write still carries the marker. A POP
    // is an external navigation, not a fresh app write — only PUSH/REPLACE writes
    // are authorized, otherwise back/forward could clobber the saved bag.
    const isAuthorized =
      navigationType !== 'POP' && isAppQueryWriteNavigation(state);

    // Track storageKey changes (cross-route navigation).
    const keyJustChanged = lastStorageKeyRef.current !== storageKey;
    if (keyJustChanged) {
      lastStorageKeyRef.current = storageKey;
      externalParamsRef.current = null;
    }

    // Gate's <Navigate> fires via useEffect — this effect may run before the URL
    // is restored. If we just arrived at this route and the bag still has data,
    // skip deletion so the Gate can complete its redirect on the next cycle.
    const isGatePendingRestore = keyJustChanged && Boolean(existingEntry);

    // Handle authorized writes: clear external mode and proceed to persistence.
    if (isAuthorized) {
      externalParamsRef.current = null;
    } else {
      // Handle unauthorized URL: detect external sources and track them.
      if (shouldSkipExternalMode(snapshot, externalParamsRef.current)) {
        return;
      }

      externalParamsRef.current = null;

      if (isUnauthorizedExternalUrl(snapshot, existingEntry)) {
        externalParamsRef.current = { ...snapshot };
        return;
      }
    }

    // Persist to storage.
    if (Object.keys(snapshot).length === 0) {
      if (isGatePendingRestore) return;
      if (!existingEntry) return;
      delete bag[storageKey];
    } else {
      bag[storageKey] = snapshot;
    }

    localStorage.setItem(
      PERSISTED_QUERY_PARAMS_STORAGE_KEY,
      JSON.stringify(bag)
    );
  }, [searchParams, storageKey, state, navigationType]);
};

export default usePersistedQueryParams;
